import React, { useEffect, useRef, useState } from 'react';

// === STYLE / MATERIAL UI ===
import { Dialog, DialogTitle, DialogActions, DialogContent, DialogContentText, Button } from '@mui/material';

// === COMPONENTS ===
import { useTaskComplete } from 'components/useTaskComplete';

// === API ===
import { on, removeListener, submitTask } from 'api/prokey/prokey.websocket';

//-----------------------------------------------------------------
function TaskCompleteModal() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const completion_status = useTaskComplete();

  const [          mode, setMode          ] = useState('');
  const [     showModal, setShowModal     ] = useState(false);
  const [hasClosedModal, setHasClosedModal] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [autoSubmitBlocked, setAutoSubmitBlocked] = useState(false);
  const taskId = useRef(null);
  
  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    if (!completion_status.is_complete) {
      setHasClosedModal(false);
    }
    const modalTimeout = setTimeout(() => setShowModal(!hasClosedModal && completion_status.is_complete), 100);

    const is_auto_mode = localStorage.getItem('auto-mode') === 'true';
    if (!autoSubmitBlocked && is_auto_mode && completion_status.is_complete) {
      submitTask();
      return () => clearTimeout(modalTimeout);
    }

    const is_batch_auto_submit = localStorage.getItem('setting-task-submit') === '1';
    const is_correct_all = mode === 'CORRECT-ALL';
    if (!autoSubmitBlocked && is_batch_auto_submit && completion_status.is_complete && !is_correct_all) {
      submitTask();
      return () => clearTimeout(modalTimeout);
    }

    if (completion_status.is_complete) {
      document.addEventListener('keydown', onKeyDown);
    } else document.removeEventListener('keydown', onKeyDown);

    return () => {
      clearTimeout(modalTimeout);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [completion_status, mode, autoSubmitBlocked, hasClosedModal]);

  useEffect(() => {
    function handleSubmitResult(data) {
      if (data.success) {
        setSubmissionError(null);
        return;
      }
      setSubmissionError(data);
      setAutoSubmitBlocked(true);
      setHasClosedModal(true);
      setShowModal(false);
    }

    function handleTask(data) {
      if (data?.id === taskId.current) return;
      taskId.current = data?.id;
      setSubmissionError(null);
      setAutoSubmitBlocked(false);
      setHasClosedModal(false);
    }

    on('TASK-SUBMIT-RESULT', handleSubmitResult);
    on('TASK_INFO', handleTask);
    return () => {
      removeListener('TASK-SUBMIT-RESULT', handleSubmitResult);
      removeListener('TASK_INFO', handleTask);
    };
  }, []);

  useEffect( () => {

    function _handleCorrectionMode(data) {
      setMode(data)
    }

    on( 'CORRECTION-MODE', _handleCorrectionMode );

    return () => {
      removeListener( 'CORRECTION-MODE', _handleCorrectionMode );
    }
  }, [])

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onKeyDown(event) {
    //-----------------------------------------------------------------
    if (event.key === 'Enter' && event.altKey) {
      submitTask();
    }
  }

  //-----------------------------------------------------------------
  function handleClose() {
    //-----------------------------------------------------------------
    setHasClosedModal(true);
    setShowModal(false);
  }

  //-----------------------------------------------------------------
  function handleSubmit() {
    //-----------------------------------------------------------------
    submitTask();
    setShowModal(false);
  }
  
  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if (submissionError) {
      return (
        <Dialog open onClose={() => setSubmissionError(null)}>
          <DialogTitle>Task cannot be completed</DialogTitle>
          <DialogContent>
            <DialogContentText>{submissionError.message}</DialogContentText>
            <ul>
              {(submissionError.validation_errors || []).map((error, index) => (
                <li key={index}>{error.message}</li>
              ))}
            </ul>
            <DialogContentText>
              Close this message to review the task. Use Show All Fields if a field is hidden. If a required field is unavailable, contact support with task ID {submissionError.id}.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSubmissionError(null)} color="primary">Close</Button>
          </DialogActions>
        </Dialog>
      );
    }
    return showModal ? (
      <Dialog open={showModal} onClose={handleClose}>
        <DialogTitle>{completion_status.empty_qc ? 'No Reviewable QC Fields' : 'Finished Processing Task'}</DialogTitle>
        <DialogContent>
          {completion_status.empty_qc ? (
            <DialogContentText>
              This QC task has no reviewable fields in its configured scope. Click 'Submit' to continue its normal workflow, or 'Close' to keep the task open.
            </DialogContentText>
          ) : (
            <>
              <DialogContentText>
                If you'd like to complete this task click 'Submit', otherwise click 'Close' to go back and review the task.
              </DialogContentText>
              <DialogContentText>
                If you don't see any fields you may need to set the correction mode to 'Show All Fields'.
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button name='back_to_task_btn' onClick={handleClose} color='primary'>
            Close
          </Button>
          <Button name='submit_task_btn' onClick={handleSubmit} color='primary'>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    ) : null;
  }

  //#endregion

  return display();
}

export { TaskCompleteModal };
